/** @namespace JQuery */
import { CustomTooltip } from './custom-tooltip.js';
import { isOrHasDomElement } from './utils.js';
/** @typedef {import('./stellarnav-types.ts').StfAfterSetupVars} StfAfterSetupVars */

/**
 * @this {HTMLElement}
 * @param {JQuery.KeyDownEvent} e - The keydown event.
 */
const handleButtonLikeKeydown = function (e) {
    if (![' ', 'Enter'].includes(e.key)) return;

    e.preventDefault();
    this.click();
};

// eslint-disable-next-line jsdoc/require-returns
/**
 * @param {HTMLElement} navElement - The DOM element for the nav
 * @param {import('./stellarnav-types.ts').StellarnavStrictOptions} settings - Stellarnav options
 */
const stellarnavFactory = (navElement, settings) => {
    const $frontPortalHeader = $('#frontPortalHeader');
    const $topHalfHeader = $('.top-header');
    const $menuToggle = $('#desktophambugericon');
    const $nav = $(navElement);
    const $firstUl = $nav.children('ul:first');
    const $body = $(document.body);
    const $document = $(document);
    const $selectedNavTabs = $nav.find(`li[data-tab-id="${settings.currentTabId}"]`);
    const $parentOfSelectedNavTab = $selectedNavTabs.parentsUntil('nav', 'li');
    const $currentRootNavTab = $nav.find(`li[data-tab-id="${settings.currentRootTabId}"]`);
    /** @type {StfAfterSetupVars} */
    const afterSetupVars = {};
    const isScreenMobileSize = () => window.innerWidth < settings.breakpoint;
    const shouldSetupForMobile = () => isScreenMobileSize() || settings.mobileMode;
    // defines top level items
    const $topLevelItems = $firstUl.children('li');
    // css classes for main menu mobile buttons
    const cssClass =
        settings.phoneBtn && settings.locationBtn
            ? 'third'
            : settings.phoneBtn || settings.locationBtn
              ? 'half'
              : 'full';
    const $lisThatHaveSubs = $nav.find('li a').next().parent('li');
    const $nestedLisWithSubs = $lisThatHaveSubs.filter(function isNotFirstLevel() {
        return !this.matches('[data-level="1"]');
    });
    const getScreenType = () => (shouldSetupForMobile() ? 'mobile' : 'desktop');

    const stickyModule = (() => {
        const $fullHeader = $('.headerclass');
        const stickyHeaderClass = 'fixed-heder';
        let isHeaderSticky = false;

        const render = () => {
            if (isHeaderSticky) {
                $fullHeader.addClass(stickyHeaderClass);
            } else {
                $fullHeader.removeClass(stickyHeaderClass);
            }
        };

        const setStickiness = () => {
            if (isHeaderSticky) return;
            isHeaderSticky = true;
            render();
        };

        const unsetStickiness = () => {
            if (!isHeaderSticky) return;
            isHeaderSticky = false;
            render();
        };

        const decideStickinessOnScroll = () => {
            if (isScreenMobileSize()) {
                unsetStickiness();
                return;
            }

            const navOffset = $nav.offset();
            const navTop = navOffset ? navOffset.top : 0;

            if (window.scrollY > navTop) {
                setStickiness();
            } else {
                unsetStickiness();
            }
        };

        const bindEvents = () => {
            window.addEventListener('scroll', decideStickinessOnScroll);
        };
        const unbindEvents = () => {
            window.removeEventListener('scroll', decideStickinessOnScroll);
        };

        const setup = () => {
            decideStickinessOnScroll();
            bindEvents();
        };

        const destroy = () => {
            unsetStickiness();
            unbindEvents();
        };

        return { setup, destroy };
    })();

    const setScreenType = () => {
        $nav.addClass(getScreenType());
    };

    const unsetScreenType = () => {
        ['desktop', 'mobile'].forEach((screenType) => $nav.removeClass(screenType));
    };

    const handleResize = () => {
        unsetScreenType();
        setScreenType();
    };

    /** @this {HTMLElement} */
    const openDD = function () {
        const $button = $(this);
        if ($button.attr('aria-expanded') === 'true') return;

        const $parentLi = $button.parent('li');
        const $dropdown = $parentLi.children('ul');

        $dropdown.stop(true, true).slideDown(settings.openingSpeed);
        $button.attr('aria-expanded', 'true');
        $parentLi.addClass('open');
    };

    /** @this {HTMLElement} */
    const closeDD = function () {
        const $button = $(this);
        if ($button.attr('aria-expanded') === 'false') return;

        const $parentLi = $button.parent('li');
        const $dropdown = $parentLi.children('ul');

        $dropdown.stop(true, true).slideUp(settings.openingSpeed);
        $button.attr('aria-expanded', 'false');
        $parentLi.removeClass('open');
    };

    /** @this {HTMLElement} */
    const toggleDD = function () {
        if ($(this).attr('aria-expanded') === 'true') {
            closeDD.call(this);
        } else {
            openDD.call(this);
        }
    };

    /**
     * @this {HTMLElement}
     * @param {JQuery.KeyDownEvent} e - The keydown event sent to the toggle button.
     */
    const handleDDToggleKeydown = function (e) {
        if (e.key === 'Escape' && $(this).attr('aria-expanded') === 'true') {
            e.stopPropagation();
            closeDD.call(this);
            return;
        }

        if (![' ', 'Enter'].includes(e.key)) return;
        e.preventDefault();
        this.click();
    };

    const commonBindEvents = () => {
        window.addEventListener('resize', handleResize);
        const { $ddToggles } = afterSetupVars;
        if (!$ddToggles) throw new TypeError('Could not bind event. $ddToggles is undefined.');
        $ddToggles.on('click', toggleDD);
        $ddToggles.on('keydown', handleDDToggleKeydown);
        $menuToggle.on('keydown', handleButtonLikeKeydown);
    };

    const commonUnbindEvents = () => {
        window.removeEventListener('resize', handleResize);
        const { $ddToggles } = afterSetupVars;
        if (!$ddToggles) throw new TypeError('Could not unbind event. $ddToggles is undefined.');
        $ddToggles.off('click', toggleDD);
        $ddToggles.off('keydown', handleDDToggleKeydown);
        $menuToggle.off('keydown', handleButtonLikeKeydown);
    };

    const commonSetup = () => {
        if (settings.sticky) stickyModule.setup();
        if (['light', 'dark'].includes(settings.theme)) $nav.addClass(settings.theme);
        if (settings.scrollbarFix) $body.addClass('stellarnav-noscroll-x');
        if (!settings.showArrows) $nav.addClass('hide-arrows');

        // adds toggle button to li items that have children
        $lisThatHaveSubs.addClass('has-sub');
        afterSetupVars.$ddToggles = $lisThatHaveSubs.children('.dd-toggle');

        setScreenType();
        commonBindEvents();

        $parentOfSelectedNavTab.addClass('active');
        $currentRootNavTab.addClass('active');
        // #endregion mark tabs active from current tab to root irrespective of primary nav alignment
    };

    const commonDestroy = () => {
        $parentOfSelectedNavTab.removeClass('active');
        $currentRootNavTab.removeClass('active');
        commonUnbindEvents();
        stickyModule.destroy();
        unsetScreenType();

        $body.removeClass('stellarnav-noscroll-x');
        $nav.removeClass('hide-arrows');
        $lisThatHaveSubs.removeClass('has-sub');
        // afterSetupVars.$ddToggles?.remove();
        // delete afterSetupVars.$ddToggles;
    };

    const topPositionModule = (() => {
        const dropLeftClass = 'drop-left';
        const $megaLis = $topLevelItems.filter('.mega');
        const $megaDropdowns = $megaLis.children('ul');
        const $megaDropdownItems = $megaDropdowns.children();
        const $ddToggleLevelOne = $topLevelItems.children('.top-dd-toggle');
        const $nestedLiTopDDToggle = $nestedLisWithSubs.children('.top-dd-toggle');
        const $nestedLiDropdowns = $nestedLisWithSubs.children('ul');
        let isModuleSetup = false;
        let isMenuOpen = false;
        let didSetupMobile = !isMenuOpen;
        /** @type {HTMLElement | undefined} */
        let openedParentLi;

        /** @this {HTMLElement} */
        const dropdownSlideDown = function () {
            $(this).children('ul').stop(true, true).slideDown(settings.openingSpeed);
        };

        /** @this {HTMLElement} */
        const dropdownSlideUp = function () {
            $(this).children('ul').stop(true, true).delay(settings.closingDelay).slideUp(settings.openingSpeed);
        };

        /**
         * @param {HTMLElement} dropdownDom - The DOM element for the dropdown
         * @returns {boolean} - Whether the dropdown has overflown to the right or not.
         */
        const hasDropdownOverflown = (dropdownDom) => {
            const allNestedDropDowns =
                /** @type {HTMLElement[]} */
                ([dropdownDom, ...dropdownDom.querySelectorAll('.drop-nav-wrapper')]);
            const bodyWidth = document.body.getBoundingClientRect().width;

            // In order to find widths of elements with display none,
            // we need to set them to display block. This doesn't show
            // up in the DOM since it's done synchronously before a browser frame.
            // All parent elements need to not be display none as well.
            allNestedDropDowns.forEach((drop) => {
                drop.dataset.initialDisplay = drop.style.display;
                drop.style.display = 'block';
            });
            const isOverflown = allNestedDropDowns.some((drop) => drop.getBoundingClientRect().right > bodyWidth);
            allNestedDropDowns.forEach((drop) => {
                const styleInitialDisplay = drop.dataset.initialDisplay;
                if (styleInitialDisplay === undefined) return;

                drop.style.display = styleInitialDisplay;
                delete drop.dataset.initialDisplay;
            });

            return isOverflown;
        };

        /**
         * @overload
         * @this {HTMLElement}
         * @returns {void}
         */
        /**
         * @this {HTMLElement}
         * @param {{focusOnToggle: boolean}} [options] - Whether to focus on toggle or not.
         */
        const closeParentDD = function (options) {
            const { focusOnToggle = false } = options ?? {};
            const $levelOneListItem = $(this);
            const $dropdown = $levelOneListItem.children('ul');

            if (!$dropdown.data('isOpen')) return;

            $dropdown
                .stop(true, true)
                .delay(settings.closingDelay)
                .slideUp(settings.openingSpeed, () => {
                    // After animation completes
                    if (!$dropdown.data('isOpen')) $levelOneListItem.removeClass(dropLeftClass);
                });
            $dropdown.data('isOpen', false);
            $ddToggleLevelOne.attr('aria-expanded', 'false');
            if (focusOnToggle) $dropdown.siblings('a[href], button').first().trigger('focus');
            openedParentLi = undefined;
        };

        /** @this {HTMLElement} */
        const openParentDD = function () {
            if (openedParentLi === this) return;
            if (openedParentLi && openedParentLi !== this) closeParentDD.call(openedParentLi);

            const $levelOneListItem = $(this);
            const $dropdown = $levelOneListItem.children('ul');
            const dropdownDom = $dropdown.get(0);

            if (!dropdownDom) return;
            // Don't wanna bother rewriting the whole section for now,
            // so just ignoring these eslint rules.
            // eslint-disable-next-line unicorn/no-this-assignment, @typescript-eslint/no-this-alias
            openedParentLi = this;
            $dropdown.data('isOpen', true);
            if (hasDropdownOverflown(dropdownDom)) $levelOneListItem.addClass(dropLeftClass);

            $dropdown.stop(true, true).slideDown(settings.openingSpeed);
            $ddToggleLevelOne.attr('aria-expanded', 'true');
        };

        // second level and below
        /** @type {Map<string, HTMLElement | undefined>} */
        const openedNestedLisByLevel = new Map();
        /**
         * @this {HTMLElement}
         * @param {{focusOnToggle: boolean}} [options] - Whether to focus on toggle or not.
         */
        const closeNestedDD = function (options) {
            const { focusOnToggle = false } = options ?? {};
            const nestedLevel = this.dataset.level;
            if (!nestedLevel) throw new Error('No level set on given element.');
            if (openedNestedLisByLevel.get(nestedLevel) === this) openedNestedLisByLevel.delete(nestedLevel);

            const $listItem = $(this);
            const $nestedDropdown = $listItem.children('ul');

            if (!$nestedDropdown.data('isOpen')) return;

            $nestedDropdown.stop(true, true).delay(settings.closingDelay).slideUp(settings.openingSpeed);
            if (focusOnToggle) $nestedDropdown.siblings('a[href], button').first().trigger('focus');
            $nestedDropdown.data('isOpen', false);
            $listItem.children('.top-dd-toggle').attr('aria-expanded', 'false');
        };

        /**
         * @this {HTMLElement}
         */
        const openNestedDD = function () {
            const nestedLevel = this.dataset.level;
            if (!nestedLevel) throw new Error('No level set on given element.');

            const sameLevelLi = openedNestedLisByLevel.get(nestedLevel);
            if (sameLevelLi && sameLevelLi !== this) closeNestedDD.call(sameLevelLi);
            openedNestedLisByLevel.set(nestedLevel, this);

            const $listItem = $(this);
            const $nestedDropdown = $listItem.children('ul');

            if ($nestedDropdown.data('isOpen')) return;

            $nestedDropdown.stop(true, true).slideDown(settings.openingSpeed);

            $nestedDropdown.data('isOpen', true);
            $listItem.children('.top-dd-toggle').attr('aria-expanded', 'true');
        };

        /**
         * @this {HTMLElement}
         */
        const lowerLevelToggle = function () {
            const $nestedDropdown = $(this).children('ul');
            if ($nestedDropdown.data('isOpen')) {
                closeNestedDD.call(this);
            } else {
                openNestedDD.call(this);
            }
        };

        /**
         * @this {HTMLElement}
         * @param {JQuery.KeyDownEvent} e - The keydown event.
         */
        const closeOnEscape = function (e) {
            e.stopPropagation();
            const liElement = this.parentElement;
            if (!(liElement instanceof HTMLElement)) return;
            if (e.key === 'Escape') closeNestedDD.call(liElement, { focusOnToggle: true });
        };

        const render = () => {
            if (isMenuOpen) {
                $firstUl.stop(true, true).slideDown(settings.openingSpeed, () => {
                    // Focus on the first focusable element for accessibility
                    // (only on mobile view)
                    if (didSetupMobile)
                        setTimeout(
                            () => $firstUl.find('a[href]:visible, button:visible').first().trigger('focus'),
                            100,
                        );
                    $firstUl.removeAttr('style');
                    isMenuOpen = true;
                });
                $menuToggle.attr('aria-expanded', 'true');
                $nav.addClass('active');
            } else {
                $firstUl.stop(true, true).slideUp(settings.openingSpeed);
                $menuToggle.attr('aria-expanded', 'false');
                $menuToggle.trigger('focus');
                $nav.removeClass('active');
            }
        };

        const closeMenu = () => {
            if (!isMenuOpen) return;
            isMenuOpen = false;
            render();
        };

        const openMenu = () => {
            if (isMenuOpen) return;
            isMenuOpen = true;
            render();
        };

        const toggleMenu = () => {
            if (isMenuOpen) {
                closeMenu();
            } else {
                openMenu();
            }
        };

        /**
         * @overload
         * @this {HTMLElement}
         * @returns {void}
         */
        /**
         * @this {HTMLElement}
         * @param {JQuery.FocusEvent} [e] - The focus event
         */
        const handleFocusOut = function (e) {
            const relTarget = e?.relatedTarget;
            if (relTarget instanceof Node && this.contains(relTarget)) return;

            closeParentDD.call(this);
        };

        /**
         * @this {HTMLElement}
         */
        const handleLevelOneToggleClick = function () {
            const listItemDom = this.parentElement;
            if (!(listItemDom instanceof HTMLElement)) return;

            if ($(listItemDom).children('ul').data('isOpen')) {
                closeParentDD.call(listItemDom);
            } else {
                openParentDD.call(listItemDom);
            }
        };

        /**
         * @this {HTMLElement}
         * @param {JQuery.KeyDownEvent} e - The Keydown event on the toggle button.
         */
        const handleLevelOneToggleKeydown = function (e) {
            const listItemDom = this.parentElement;
            if (!(listItemDom instanceof HTMLElement)) return;

            if (e.key === 'Escape') closeParentDD.call(listItemDom);
        };

        /** @this {HTMLElement} */
        const handleNestedToggleClick = function passProperThis() {
            const liElement = this.parentElement;
            if (!(liElement instanceof HTMLElement)) return;
            lowerLevelToggle.call(liElement);
        };

        /**
         * @this {HTMLElement}
         * @param {JQuery.KeyDownEvent} e - The keydown event
         */
        const handleNestedToggleKeydown = function (e) {
            if (this.getAttribute('aria-expanded') !== 'true' || e.key !== 'Escape') return;
            const liElement = this.parentElement;
            if (!liElement) return;

            e.stopPropagation();
            closeNestedDD.call(liElement);
        };
        /**
         * @this {HTMLElement}
         * @param {JQuery.FocusOutEvent} e - The FocusOut event.
         */
        const handleNestedDDFocusOut = function (e) {
            const relTarget = e.relatedTarget;
            if (relTarget instanceof Node && this.contains(relTarget)) return;

            const liElement = this.parentElement;
            if (!(liElement instanceof HTMLElement)) throw new Error('Could not find the containing li element.');
            if (relTarget instanceof Node && liElement.parentElement?.contains(relTarget)) e.stopPropagation();

            closeNestedDD.call(liElement);
        };

        const bindDesktopEvents = () => {
            // mega dropdowns
            $megaLis.on('mouseenter', dropdownSlideDown);
            $megaLis.on('mouseleave', dropdownSlideUp);
            // first level
            $topLevelItems.on('mouseenter', openParentDD);
            $topLevelItems.on('mouseleave', closeParentDD);
            $topLevelItems.on('focusout', handleFocusOut);

            $ddToggleLevelOne.on('click', handleLevelOneToggleClick);
            $ddToggleLevelOne.on('keydown', handleLevelOneToggleKeydown);

            // second level and below
            $nestedLisWithSubs.on('mouseenter', openNestedDD);
            // @ts-expect-error - The handler doesn't conform to the available overloads for some reason. Temporarily ignored.
            $nestedLisWithSubs.on('mouseleave', closeNestedDD);

            $nestedLiTopDDToggle.on('click', handleNestedToggleClick);
            $nestedLiTopDDToggle.on('keydown', handleNestedToggleKeydown);
            $nestedLiDropdowns.on('keydown', closeOnEscape);
            $nestedLiDropdowns.on('focusout', handleNestedDDFocusOut);
        };

        const unbindDesktopEvents = () => {
            // megas
            $megaLis.off('mouseenter', dropdownSlideDown);
            $megaLis.off('mouseleave', dropdownSlideUp);

            // first level
            $topLevelItems.off('mouseenter', openParentDD);
            $topLevelItems.off('mouseleave', closeParentDD);
            $topLevelItems.off('focusout', handleFocusOut);

            $ddToggleLevelOne.off('click', handleLevelOneToggleClick);
            $ddToggleLevelOne.off('keydown', handleLevelOneToggleKeydown);

            // second level and below
            $nestedLisWithSubs.off('mouseenter', openNestedDD);
            // @ts-expect-error - The handler doesn't conform to the available overloads for some reason. Temporarily ignored.
            $nestedLisWithSubs.off('mouseleave', closeNestedDD);

            $nestedLiTopDDToggle.off('click', handleNestedToggleClick);
            $nestedLiTopDDToggle.off('keydown', handleNestedToggleKeydown);
            $nestedLiDropdowns.off('keydown', closeOnEscape);
            $nestedLiDropdowns.off('focusout', handleNestedDDFocusOut);
        };

        const setupMobile = () => {
            if (didSetupMobile) return;
            didSetupMobile = true;

            unbindDesktopEvents();
            closeMenu();
        };

        const setupDesktop = () => {
            if (!didSetupMobile) return;
            didSetupMobile = false;

            bindDesktopEvents();
            openMenu();
        };

        const setMenuOnWinSize = () => {
            if (isScreenMobileSize()) {
                setupMobile();
            } else {
                setupDesktop();
            }
        };

        const bindEvents = () => {
            $menuToggle.on('click', toggleMenu);
            window.addEventListener('resize', setMenuOnWinSize);
        };

        const unbindEvents = () => {
            unbindDesktopEvents();
            $menuToggle.off('click', toggleMenu);
            window.removeEventListener('resize', setMenuOnWinSize);
        };

        const baseTopSetup = () => {
            commonSetup();

            $nav.addClass('top');

            // calculates the nav width based on the sum of all top-level items
            const navWidth = Math.round(
                $topLevelItems
                    .get()
                    .reduce(
                        (totalWidth, topLevelItemDom) => totalWidth + topLevelItemDom.getBoundingClientRect().width,
                        0,
                    ),
            );

            $megaLis.each((_parentItemIndex, topLevelItemDom) => {
                const $topLevelItem = $(topLevelItemDom);
                const $singleMegaDropdownItems = $topLevelItem.children('ul:first').children();

                const numColsFromAttribute = Number($topLevelItem.attr('data-columns'));
                const numCols = [2, 3, 4, 5, 6, 7, 8].includes(numColsFromAttribute) ? numColsFromAttribute : 4;

                // gets the data-column attribute and divides the columns equally
                $singleMegaDropdownItems.width(`${100 / numCols}%`);
            });

            // defines the mega dropdown width to be the same as nav width
            $megaDropdowns.css({
                'max-width': navWidth,
                // left aligns mega dropdown with nav
                left: 0,
                right: 0,
                margin: '0 auto',
            });
        };

        const baseTopDestroy = () => {
            $megaDropdowns.removeAttr('style');
            $megaDropdownItems.width('');
        };

        const menuHiddenSetup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            baseTopSetup();
            bindDesktopEvents();
        };

        const menuHiddenDestroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            baseTopDestroy();
            unbindDesktopEvents();
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            baseTopSetup();
            bindEvents();
            render();
            setMenuOnWinSize();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            baseTopDestroy();
            unbindEvents();
            commonDestroy();
        };

        if (settings.menuHidden) {
            return {
                setup() {
                    menuHiddenSetup();
                },
                destroy() {
                    menuHiddenDestroy();
                },
            };
        }

        return { setup, destroy, openMenu, closeMenu, toggleMenu };
    })();

    /** @type {import('./stellarnav-types.ts').HorizontalModule} */
    const horizontalModule = (() => {
        let isModuleSetup = false;
        const navDirection = settings.position.replace('fixed', '');
        const phoneButtonClass = 'call-btn-mobile';
        const locationButtonClass = 'location-btn-mobile';
        const closeButtonClass = 'close-menu';
        const closeButtonHtml = `
            <li>
                <button
                    type="button"
                    aria-label="Close"
                    class="${closeButtonClass} ${cssClass}"
                >
                    <span class="icon-close" aria-hidden="true"></span>
                    ${settings.closeLabel}
                </button>
            </li>`;
        const phoneButtonHtml = `
            <a href="tel:${settings.phoneBtn}" class="${phoneButtonClass} ${cssClass}">
                <svg id="icon-phone"></svg>
            </a>`;
        const locationButtonHtml = `
            <a href="${settings.locationBtn}" class="${locationButtonClass} ${cssClass}" target="_blank">
                <svg id="icon-location"></svg>
            </a>`;
        const $expandOnLoad = $('.expand-on-load');

        const bindEvents = () => {
            const { $menuCloseButton } = afterSetupVars;
            if (!$menuCloseButton) throw new Error('Could not bind events. $menuCloseButton is undefined.');

            $menuCloseButton.on('keydown', handleButtonLikeKeydown);
        };

        const unbindEvents = () => {
            const { $menuCloseButton } = afterSetupVars;
            if ($menuCloseButton) {
                $menuCloseButton.off('keydown', handleButtonLikeKeydown);
            }
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            commonSetup();
            $firstUl.prepend(closeButtonHtml);
            afterSetupVars.$menuCloseButton = $firstUl.find(`.${closeButtonClass}`);

            if (settings.locationBtn) {
                $firstUl.prepend(locationButtonHtml);
                afterSetupVars.$locationButton = $firstUl.children(`.${locationButtonClass}`);
            }

            if (settings.phoneBtn) {
                $firstUl.prepend(phoneButtonHtml);
                afterSetupVars.$phoneButton = $firstUl.children(`.${phoneButtonClass}`);
            }

            $nav.addClass(navDirection);

            // #region mark tabs active from current tab to root irrespective of primary nav alignment
            if (!settings.menuHidden) {
                $parentOfSelectedNavTab.children('a.dd-toggle').trigger('click');
            }

            $expandOnLoad.css('display', 'block');
            $expandOnLoad.parent().addClass('open');

            bindEvents();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            $expandOnLoad.css('display', '');
            $expandOnLoad.parent().removeClass('open');

            commonDestroy();

            if (afterSetupVars.$menuCloseButton) {
                afterSetupVars.$menuCloseButton.remove();
                delete afterSetupVars.$menuCloseButton;
            }

            if (afterSetupVars.$locationButton) {
                afterSetupVars.$locationButton.remove();
                delete afterSetupVars.$locationButton;
            }

            if (afterSetupVars.$phoneButton) {
                afterSetupVars.$phoneButton.remove();
                delete afterSetupVars.$phoneButton;
            }

            $nav.removeClass(navDirection);
            unbindEvents();
        };

        return { setup, destroy };
    })();

    const commonFixedHorizontalModule = (() => {
        let isModuleSetup = false;
        const bodyClass = `${settings.position}-navigation-open-body`;
        const fpHeaderClass = `${settings.position.replace('fixed', '')}-navigation-open`;

        // #region fixing left and right menus script
        const $frontPortalBody = $('div#frontPortalBody');
        const $fullHeader = $('nav.headerclass');

        const alignLayout = () => {
            const fullHeaderHeight = $fullHeader.outerHeight() || 0;
            const topHalfHeaderHeight = $topHalfHeader.outerHeight() || 0;
            const bodyGapFromHeader = 20;

            $frontPortalBody.css('padding-top', `${fullHeaderHeight + bodyGapFromHeader}px`);
            $menuToggle.css('top', `${topHalfHeaderHeight}px`);
            afterSetupVars.$localMenuToggle?.css('top', `${topHalfHeaderHeight}px`);

            $firstUl.css({
                height: `${window.innerHeight - topHalfHeaderHeight}px`,
                top: `${topHalfHeaderHeight}px`,
            });
            $firstUl.scrollTop(0);
        };

        const bindEvents = () => {
            window.addEventListener('resize', alignLayout);
        };

        const unbindEvents = () => {
            window.removeEventListener('resize', alignLayout);
        };

        return {
            setup() {
                if (isModuleSetup) return;
                isModuleSetup = true;

                horizontalModule.setup();
                $body.addClass(bodyClass);
                $frontPortalHeader.addClass(fpHeaderClass);
                alignLayout();
                bindEvents();
            },
            destroy() {
                if (!isModuleSetup) return;
                isModuleSetup = false;

                $frontPortalBody.css('padding-top', '');
                $menuToggle.css('top', '');
                $firstUl.css({
                    height: '',
                    top: '',
                });

                unbindEvents();
                $body.removeClass(bodyClass);
                $frontPortalHeader.removeClass(fpHeaderClass);
                horizontalModule.destroy();
            },
        };
    })();

    const fixedHorizontalModule = (() => {
        let isMenuOpen = true;
        let isModuleSetup = false;
        const menuOpenClass = 'open';
        // MenuToggle just for this position module
        const localMenuToggleHTML = `
                <button class="fixed-horizontal-menu-toggle" type="button" aria-label="Toggle Navigation">
                    <svg
                        class="hambuger__horizontal-svg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M3.0675 21C3.0675 21 3 20.97 3 20.9325L3 3.06C3 3.06 3.03 2.9925 3.0675 2.9925L20.9325 2.9925C20.9325 2.9925 21 3.0225 21 3.06L21 20.9325C21 20.9325 20.97 21 20.9325 21L3.06 21M3.06 22.5L20.9325 22.5C21.795 22.5 22.5 21.8025 22.5 20.9325L22.5 3.06C22.5 2.1975 21.8025 1.4925 20.9325 1.4925L3.06 1.4925C2.1975 1.4925 1.4925 2.19 1.4925 3.06L1.4925 20.9325C1.4925 21.795 2.19 22.5 3.06 22.5Z"
                            class="ExpandCollapseIcon__Outline-sc-1vxtlu9-1 dNHHvL"
                        ></path>
                        <path
                            d="M9.2325 7.6875L13.845 12.0375L9.315 16.3125C8.94 16.6725 8.9175 17.265 9.2775 17.64C9.63 18.015 10.2225 18.03 10.5975 17.6775L15.8475 12.7275C16.2225 12.3825 16.245 11.7975 15.9 11.4225C15.8925 11.4075 15.885 11.4 15.87 11.385C15.84 11.3475 15.81 11.31 15.7725 11.2725L10.5225 6.3225C10.3425 6.1575 10.11 6.0675 9.8775 6.0675C9.63 6.0675 9.3825 6.1725 9.195 6.3675C8.8425 6.7425 8.8575 7.335 9.2325 7.6875Z"
                            class="ExpandCollapseIcon__InnerArrow-sc-1vxtlu9-0 cobYFX"
                        ></path>
                    </svg>
                </button>
            `;

        const getTooltipTitle = () => (isMenuOpen ? 'Collapse' : 'Expand');

        const render = () => {
            if (afterSetupVars.hamburgerTooltip) {
                afterSetupVars.hamburgerTooltip.hide();
                afterSetupVars.hamburgerTooltip.setTitle(getTooltipTitle());
            }

            if (isMenuOpen) {
                $body.addClass(menuOpenClass);
            } else {
                $body.removeClass(menuOpenClass);
            }

            afterSetupVars.$localMenuToggle?.attr('aria-expanded', isMenuOpen.toString());
        };

        const openMenu = () => {
            if (isMenuOpen) return;
            isMenuOpen = true;
            render();
        };

        const closeMenu = () => {
            if (!isMenuOpen) return;
            isMenuOpen = false;
            render();
        };

        const toggleMenu = () => {
            if (isMenuOpen) {
                closeMenu();
            } else {
                openMenu();
            }
        };

        const bindEvents = () => {
            afterSetupVars.$localMenuToggle?.on('click', toggleMenu);
        };

        const unbindEvents = () => {
            afterSetupVars.$localMenuToggle?.off('click', toggleMenu);
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            $nav.prepend(localMenuToggleHTML);
            afterSetupVars.$localMenuToggle = $('.fixed-horizontal-menu-toggle');

            bindEvents();

            commonFixedHorizontalModule.setup();
            const menuToggleDom = afterSetupVars.$localMenuToggle.get(0);
            if (!menuToggleDom) throw new TypeError('MenuToggle was not found.');
            afterSetupVars.hamburgerTooltip = new CustomTooltip(menuToggleDom, {
                placement: /** @type {'left' | 'right'} */ (
                    settings.position.replace('fixed', '') === 'left' ? 'right' : 'left'
                ),
                title: getTooltipTitle(),
            });

            render();
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            commonFixedHorizontalModule.destroy();
            afterSetupVars.hamburgerTooltip?.dispose();
            afterSetupVars.$localMenuToggle?.remove();

            unbindEvents();
        };

        return { setup, destroy };
    })();

    const unfixedHorizontalModule = (() => {
        let isMenuOpen = false;
        let isModuleSetup = false;
        const bodyClass = 'top-navigation-open-body';
        const fpHeaderClass = `${settings.position}-navigation-open`;

        const render = () => {
            if (isMenuOpen) {
                $firstUl.stop(true, true).fadeIn(settings.openingSpeed, () => {
                    // Focus on the first focusable element for accessibility
                    setTimeout(
                        () =>
                            $firstUl
                                .find('a[href]:visible, button:visible, [tabindex="0"]:visible')
                                .first()
                                .trigger('focus'),
                        100,
                    );
                    $firstUl.removeAttr('style');
                    isMenuOpen = true;
                });
                $menuToggle.attr('aria-expanded', 'true');
                $nav.addClass('active');
            } else {
                $firstUl.stop(true, true).fadeOut(settings.openingSpeed);
                $menuToggle.attr('aria-expanded', 'false');
                $menuToggle.trigger('focus');
                $nav.removeClass('active');
            }
        };

        const closeMenu = () => {
            if (!isMenuOpen) return;
            isMenuOpen = false;
            render();
        };

        const openMenu = () => {
            if (isMenuOpen) return;
            isMenuOpen = true;
            render();
        };

        const toggleMenu = () => {
            if (isMenuOpen) {
                closeMenu();
            } else {
                openMenu();
            }
        };

        /** @param {JQuery.ClickEvent} event - The click event. */
        const closeOnClickOut = (event) => {
            // We only care about DOM elements
            if (!(event.target instanceof Element)) return;
            const eventTarget = event.target;
            // Checks if clicked out of nav and not clicked on the toggle button
            // Closing on toggle click would mean not opening the menu, ever.
            if (isOrHasDomElement($nav, eventTarget) || isOrHasDomElement($menuToggle, eventTarget)) return;
            closeMenu();
        };

        /**
         * @this {HTMLElement}
         * @param {JQuery.FocusOutEvent} e - The focus out event.
         */
        const closeMenuOnFocusOut = function (e) {
            const relTarget = e.relatedTarget;
            const menuToggleDom = $menuToggle.get(0);
            if (!(relTarget instanceof Node)) return;
            if (!menuToggleDom) throw new Error('Could not find menu toggle from the DOM.');

            if (this.contains(relTarget) || menuToggleDom.contains(relTarget)) return;
            closeMenu();
        };

        /**
         * @param {JQuery.KeyDownEvent} e - The keydown event.
         */
        const closeOnEscape = (e) => {
            if (e.key === 'Escape') closeMenu();
        };

        const bindEvents = () => {
            $document.on('click', closeOnClickOut);
            $menuToggle.on('click', toggleMenu);

            const { $menuCloseButton } = afterSetupVars;
            if (!$menuCloseButton) throw new TypeError('Failed to bind event. $menuCloseButton is undefined');
            $menuCloseButton.on('click', closeMenu);
            $menuCloseButton.on('keydown', handleButtonLikeKeydown);
            $firstUl.on('focusout', closeMenuOnFocusOut);
            $firstUl.on('keydown', closeOnEscape);
        };

        const unbindEvents = () => {
            $document.off('click', closeOnClickOut);
            $menuToggle.off('click', toggleMenu);

            const { $menuCloseButton } = afterSetupVars;
            if ($menuCloseButton) {
                $menuCloseButton.off('click', closeMenu);
                $menuCloseButton.off('keydown', handleButtonLikeKeydown);
            }
            $firstUl.off('focusout', closeMenuOnFocusOut);
            $firstUl.off('keydown', closeOnEscape);
        };

        const setup = () => {
            if (isModuleSetup) return;
            isModuleSetup = true;

            horizontalModule.setup();
            bindEvents();
            render();
            $body.addClass(bodyClass);
            $frontPortalHeader.addClass(fpHeaderClass);

            if (/right/.test(settings.position)) {
                $topHalfHeader.append($menuToggle);
            }
        };

        const destroy = () => {
            if (!isModuleSetup) return;
            isModuleSetup = false;

            $body.removeClass(bodyClass);
            $frontPortalHeader.removeClass(fpHeaderClass);
            unbindEvents();
            horizontalModule.destroy();
        };

        return { setup, destroy, openMenu, closeMenu, toggleMenu };
    })();

    return {
        unfixedHorizontalModule,
        horizontalModule,
        commonFixedHorizontalModule,
        fixedHorizontalModule,
        topPositionModule,
    };
};

export { stellarnavFactory };
